import { CalendarClock, CheckCircle, Loader2, X } from 'lucide-react';
import { fromZonedTime, format as tzFormat } from 'date-fns-tz';

import { ScheduleEnvironment } from '@/react/portainer/environments/update-schedules/DetailsView/types';
import { StatusType } from '@/react/edge/edge-stacks/types';

import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { parseEnvironmentTimezone } from '../common/utils';

export function envStatus(scheduleEnvironment: ScheduleEnvironment) {
  if (!scheduleEnvironment?.edgeStack || !scheduleEnvironment?.environment) {
    return null;
  }

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const environmentTimeZone = parseEnvironmentTimezone(
    scheduleEnvironment.environment.LocalTimeZone
  );
  const scheduledDate = fromZonedTime(
    scheduleEnvironment?.scheduledTime ?? '',
    environmentTimeZone
  );
  const localScheduledDate = fromZonedTime(scheduledDate, timeZone);

  const status =
    scheduleEnvironment.edgeStack?.Status?.[
      scheduleEnvironment.environment.Id
    ] ?? null;

  const lastStatus = status?.Status?.[status.Status.length - 1] ?? null;
  if (!lastStatus) {
    if (localScheduledDate > new Date()) {
      return (
        <Badge type="indigo" className="flex gap-1">
          <Icon icon={CalendarClock} />
          <div>Scheduled</div>
        </Badge>
      );
    }

    return (
      <Badge type="info" className="flex gap-1">
        <Icon icon={Loader2} spin mode="primary" />
        <div>In progress</div>
      </Badge>
    );
  }

  const formatted = tzFormat(
    new Date(lastStatus.Time * 1000),
    'yyyy-MM-dd HH:mm:ss',
    { timeZone }
  );

  const hasError = status?.Status.some(
    (envStatus) => envStatus.Type === StatusType.Error
  );
  if (hasError) {
    return (
      <TooltipWithChildren message={formatted}>
        <div className="flex">
          <Badge type="danger" className="flex gap-1">
            <Icon icon={X} />
            <div>Failed</div>
          </Badge>
        </div>
      </TooltipWithChildren>
    );
  }

  const completed = lastStatus?.Type === StatusType.RemoteUpdateSuccess;
  if (completed) {
    return (
      <div className="flex">
        <Badge type="success" className="flex gap-1">
          <Icon icon={CheckCircle} />
          <div>Completed</div>
        </Badge>
      </div>
    );
  }

  return (
    <Badge type="info" className="flex gap-1">
      <Icon icon={Loader2} spin mode="primary" />
      <div>In progress</div>
    </Badge>
  );
}
